import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../hooks/use-auth";
import { useSingleton } from "../../hooks/use-singleton";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTE_PERSONAL_JOURNEY } from "../../route";
import { ROUTE_DASHBOARD } from "../../route/studentRoute";
import { ROUTE_DASHBOARD as TnpscRoute } from "../../route/tnpscRoute";
import { ROUTE_DASHBOARD as NeetJ2ERoute } from "../../route/neetJteRoute";
import { ROUTE_DASHBOARD as BankinRoutes } from "../../route/bankingRoute";
import {
  ROUTE_STUDENT_MANAGEMENT,
  ROUTE_DASHBOARD as ROUTE_USER_DASHBOARD,
} from "../../route";
import { ROUTE_DASHBOARD as teacherRouter } from "../../route/teacherRoute";
import { ROUTE_DASHBOARD as communicateDasboard } from "../../route/communicative";
import { useApi } from "../../hooks/use-api";
import { API_TEMP_LOGIN } from "../../utils/api-routes";

const DEFAULT_ERROR = {
  errEmail: "",
  errPassword: "",
  errUserName: "",
  isClicked: false,
  isValid: false,
};

const useHandler = () => {
  const { post } = useApi();
  const { login, requestPasswordReset, persistSession, isLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const isEmail = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

  const [data, setData] = useState({
    email: "",
    password: "",
    username: "",
    check: false,
  });
  const [error, setError] = useState(DEFAULT_ERROR);

  const { role, board, examType } = useSingleton();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [showSignupSuccess, setShowSignupSuccess] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [shouldRenderSplash, setShouldRenderSplash] = useState(true);
  const [loginType, setLoginType] = useState("email");
  const [loading, setLoading] = useState(false);

  const { email, password, username } = data;
  const { isClicked } = error;

  const onChangeValue = useCallback((e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: name === "email" || name === "password" ? value.trim() : value,
    }));
  }, []);

  const toggleCheck = useCallback(() => {
    setData((prev) => ({ ...prev, check: !prev.check }));
  }, []);

  const onValidate = useCallback(() => {
    let errState = { ...DEFAULT_ERROR, isValid: true, isClicked: true };

    if (!password) {
      errState = {
        ...errState,
        errPassword: "Please Enter Password",
        isValid: false,
      };
    }

    if (!username) {
      errState = {
        ...errState,
        errUserName: "Please Enter Username or phonenumber",
        isValid: false,
      };
    }

    // if (loginType === "email") {
    //   if (!isEmail.test(email)) {
    //     errState = {
    //       ...errState,
    //       errEmail: "Please enter a valid email",
    //       isValid: false,
    //     };
    //   }
    // } else {
    //   if (!username) {
    //     errState = {
    //       ...errState,
    //       errUserName: "Please Enter Username or phonenumber",
    //       isValid: false,
    //     };
    //   }
    // }

    setError(errState);
    return errState;
  }, [username, password]);

  useEffect(() => {
    if (isClicked) onValidate();
  }, [data, isClicked, onValidate]);

  const navigateByRole = (userData) => {
    const role = userData?.role;
    const board = userData?.board;
    const examType = userData?.focus;

    if (role === "student") {
      navigate(ROUTE_USER_DASHBOARD, { replace: true });
    } else if (role === "aspirant" && examType?.startsWith("TNPSC")) {
      navigate(TnpscRoute, { replace: true });
    } else if (role === "aspirant" && examType?.startsWith("UPSC")) {
      navigate(ROUTE_DASHBOARD, { replace: true });
    } else if (role === "aspirant" && (board === "NEET" || board === "JEE")) {
      navigate(NeetJ2ERoute, { replace: true });
    } else if (role === "admin") {
      navigate(ROUTE_STUDENT_MANAGEMENT, { replace: true });
    } else if (role === "guest" && examType?.startsWith("TNPSC")) {
      navigate(TnpscRoute, { replace: true });
    } else if (role === "guest" && examType?.startsWith("UPSC")) {
      navigate(ROUTE_DASHBOARD, { replace: true });
    } else if (
      role === "guest" &&
      (examType?.startsWith("NEET") || examType?.startsWith("JEE"))
    ) {
      navigate(NeetJ2ERoute, { replace: true });
    } else if (
      role === "guest" &&
      (examType?.startsWith("CBSE") || examType?.startsWith("TN_STATE_BOARD"))
    ) {
      navigate(ROUTE_USER_DASHBOARD, { replace: true });
    } else if (
      role === "aspirant" &&
      (board === "Banking" || examType?.startsWith("Banking"))
    ) {
      navigate(BankinRoutes, { replace: true });
    } else if (role === "teacher") {
      navigate(teacherRouter, { replace: true });
    } else if (
      role === "ce_user" &&
      examType?.startsWith("Communicative English")
    ) {
      navigate(communicateDasboard, { replace: true });
    } else if (
      role === "guest" &&
      examType?.startsWith("Communicative English")
    ) {
      navigate(communicateDasboard, { replace: true });
    }
  };

  const onSubmit = async () => {
    const { isValid } = onValidate();
    if (!isValid) return;

    const isMail = loginType === "email" ? email : username;

    setLoading(true);

    try {
      const res = await post(API_TEMP_LOGIN, {
        identifier: username.trim(),
        password: password.trim(),
      });

      if (res) {
        persistSession(res?.data);
        setLoading(false);
        const isPreference = res?.data?.is_learning_preference ?? true;
        const role = res?.data?.role;
        if (!isPreference && role !== "admin") {
          navigate(ROUTE_PERSONAL_JOURNEY);
        } else {
          navigateByRole(res?.data);
        }
      }
    } catch (err) {
      setLoading(false);
      // Error handled by useAuth
    }
  };

  const onResetPassword = async () => {
    if (!email || !isEmail.test(email)) {
      setError((prev) => ({
        ...prev,
        errEmail: "Please enter a valid email to reset password",
        isClicked: true,
      }));
      return;
    }
    await requestPasswordReset(email);
  };

  // Show success banner if redirected from signup
  useEffect(() => {
    if (location.state?.signupSuccess) {
      setShowSignupSuccess(true);
      setIsSignUpMode(false); // ensure we're on sign-in view
      const t = setTimeout(() => setShowSignupSuccess(false), 5000);
      return () => clearTimeout(t);
    }
  }, [location.state]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
      // Wait for fade out animation to finish before removing from DOM
      setTimeout(() => setShouldRenderSplash(false), 800);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  return {
    data,
    showPassword,
    error,
    isLoading,
    shouldRenderSplash,
    showSplash,
    loginType,
    role,
    board,
    examType,
    isSignUpMode,
    loading,
    setIsSignUpMode,
    onSubmit,
    onChangeValue,
    toggleCheck,
    setShowPassword,
    onResetPassword,
    navigate,
    setLoginType,
  };
};

export default useHandler;
